// hooks/usePhoneVerification.ts
// Hook to handle phone number verification via Twilio Verify
import { useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { formatPhoneNumberForAPI } from "../utils/phoneValidation";

type VerificationStatus = "idle" | "sending" | "sent" | "verifying" | "verified" | "error";

interface UsePhoneVerificationOptions {
  context?: "delivery_address" | "vendor";
  userId?: string;
  onVerified?: () => void;
}

export function usePhoneVerification(options: UsePhoneVerificationOptions = {}) {
  const { context, userId, onVerified } = options;

  const [status, setStatus] = useState<VerificationStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);

  // Send verification code to phone number
  const sendVerificationCode = useCallback(async (phoneNumber: string): Promise<boolean> => {
    setStatus("sending");
    setError(null);

    try {
      // Format phone for API (E.164)
      const formattedPhone = formatPhoneNumberForAPI(phoneNumber);
      
      if (!formattedPhone) {
        setError("Invalid phone number");
        setStatus("error");
        return false;
      }

      const { data, error: invokeError } = await supabase.functions.invoke("send-verification-code", {
        body: { phone_number: formattedPhone },
      });

      if (invokeError) {
        console.error("[PhoneVerification] Error:", invokeError);
        setError(invokeError.message || "Failed to send verification code");
        setStatus("error");
        return false;
      }

      if (!data?.success) {
        setError(data?.error || "Failed to send verification code");
        setStatus("error");
        return false;
      }

      setStatus("sent");
      return true;
    } catch (err: any) {
      console.error("[PhoneVerification] Exception:", err);
      setError(err?.message || "An error occurred");
      setStatus("error");
      return false;
    }
  }, []);

  // Verify the code
  const verifyCode = useCallback(async (phoneNumber: string, code: string): Promise<boolean> => {
    setStatus("verifying");
    setError(null);

    try {
      // Format phone for API (E.164)
      const formattedPhone = formatPhoneNumberForAPI(phoneNumber);
      
      if (!formattedPhone) {
        setError("Invalid phone number");
        setStatus("error");
        return false;
      }

      const { data, error: invokeError } = await supabase.functions.invoke("verify-phone-code", {
        body: {
          phone_number: formattedPhone,
          code,
          user_id: userId,
          context,
        },
      });

      if (invokeError) {
        console.error("[PhoneVerification] Verify error:", invokeError);
        setError(invokeError.message || "Failed to verify code");
        setStatus("error");
        return false;
      }

      if (!data?.success || !data?.verified) {
        setError(data?.error || "Invalid verification code");
        setStatus("error");
        return false;
      }

      setStatus("verified");
      setVerifiedPhone(formattedPhone);
      onVerified?.();
      return true;
    } catch (err: any) {
      console.error("[PhoneVerification] Verify exception:", err);
      setError(err?.message || "An error occurred");
      setStatus("error");
      return false;
    }
  }, [context, userId, onVerified]);

  // Reset state
  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
    setVerifiedPhone(null);
  }, []);

  // Check if a phone number is the currently verified one
  const isPhoneVerified = useCallback((phoneNumber: string): boolean => {
    if (!verifiedPhone) return false;
    const formattedPhone = formatPhoneNumberForAPI(phoneNumber);
    return formattedPhone === verifiedPhone;
  }, [verifiedPhone]);

  return {
    status,
    error,
    verifiedPhone,
    isVerified: status === "verified",
    isSending: status === "sending",
    isVerifying: status === "verifying",
    codeSent: status === "sent" || status === "verifying" || status === "verified",
    sendVerificationCode,
    verifyCode,
    reset,
    isPhoneVerified,
  };
}

